import { useMemo } from "react";
import type { DemoAttendeeRow, LeadCalRow } from "../sql";
import { uniqueDemos } from "../helpers";
import { KpiCard, HBarChart } from "../../../lib/funnel/charts";
import { fmtN, fmtPct, fmtDateShort } from "../../../lib/funnel/helpers";
import { InfoButton } from "../../../lib/funnel/InfoButton";
import { FilterableTable } from "../../../lib/funnel/FilterableTable";
import type { ColumnSpec } from "../../../lib/funnel/FilterableTable";
import { SectionCardHeader } from "../../../components/ChartCard";
import type { FilterPillSpec } from "../../../components/FilterPills";

// Booking-side view, sourced from reporting.lead_cal. Numbers in this section
// are not derived from the main demo spine, and the matched-subset views
// carry "may not generalise" disclaimers.
export function LeadCalSupplementary({
  demoRows,
  leadCalRows,
  pagePills,
}: {
  demoRows: DemoAttendeeRow[];
  leadCalRows: LeadCalRow[];
  pagePills?: FilterPillSpec[];
}) {
  const stats = useMemo(() => {
    const demos = uniqueDemos(demoRows);
    const totalDemos = demos.length;
    const demosWithMatch = demos.filter((d) => d.has_lead_cal_match).length;
    const totalBookings = leadCalRows.length;
    const matchedReal = leadCalRows.filter((b) => b.matched_real_demo).length;
    const matchedNoShow = leadCalRows.filter((b) => b.matched_no_show_only).length;
    const unmatched = leadCalRows.filter((b) => b.unmatched).length;
    const withUtm = leadCalRows.filter((b) => b.has_utm_source).length;
    // Bookings in the most recent calendar month present in the data. Using
    // the latest booking timestamp keeps this responsive when the cohort
    // filter slides earlier/later, rather than locking it to "now".
    const months = new Set<string>();
    for (const b of leadCalRows) {
      if (b.booked_at) months.add(b.booked_at.slice(0, 7));
    }
    const latestMonth = Array.from(months).sort().pop() ?? null;
    const bookingsThisMonth = latestMonth
      ? leadCalRows.filter((b) => b.booked_at && b.booked_at.slice(0, 7) === latestMonth).length
      : 0;
    return { totalDemos, demosWithMatch, totalBookings, matchedReal, matchedNoShow, unmatched, withUtm, bookingsThisMonth, latestMonth };
  }, [demoRows, leadCalRows]);

  const channelData = useMemo(() => {
    const counts = new Map<string, number>();
    for (const b of leadCalRows) counts.set(b.channel_bucket, (counts.get(b.channel_bucket) ?? 0) + 1);
    return Array.from(counts.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [leadCalRows]);

  const matchedChannelFunnel = useMemo(() => {
    const matched = leadCalRows.filter((b) => b.matched_real_demo);
    const counts = new Map<string, { bookings: number; signedUp: number; paid: number }>();
    for (const b of matched) {
      const cur = counts.get(b.channel_bucket) ?? { bookings: 0, signedUp: 0, paid: 0 };
      cur.bookings += 1;
      if (b.booker_signed_up) cur.signedUp += 1;
      if (b.booker_company_on_paid_plan) cur.paid += 1;
      counts.set(b.channel_bucket, cur);
    }
    return Array.from(counts.entries())
      .map(([label, v]) => ({ label, ...v }))
      .filter((r) => r.bookings >= 3)
      .sort((a, b) => b.bookings - a.bookings);
  }, [leadCalRows]);

  const noShowRecovery = useMemo(() => {
    return leadCalRows.filter((b) => (b.matched_no_show_only || b.unmatched) && !b.booker_signed_up).length;
  }, [leadCalRows]);

  return (
    <section className="card" style={{ background: "var(--bg-subtle, #FAFAF7)" }}>
      <SectionCardHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Lead_cal supplementary
            <InfoButton label="Lead_cal panel">
              <p style={{ margin: "0 0 6px" }}>
                These numbers come from <code>reporting.lead_cal</code> (Cal.com bookings), not the main demo spine.
              </p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>Linkage health is a data-quality KPI, not a funnel KPI. Push the matched-real-demo % up by fixing UTM tagging upstream.</li>
                <li>The matched-subset booking → demo conversion view only covers ~44% of bookings. The remaining 56% may behave differently.</li>
                <li>No-show recovery is matched_no_show_only + unmatched bookings whose booker never signed up.</li>
              </ul>
            </InfoButton>
          </span>
        }
        subtitle="Booking-side view. Use to spot UTM gaps and prioritise no-show outreach."
        filterPills={pagePills}
      />

      <div className="kpi-grid" style={{ margin: "8px 16px 0" }}>
        <KpiCard
          label="Bookings this month"
          value={fmtN(stats.bookingsThisMonth)}
          sub={stats.latestMonth ?? "—"}
          tone="primary"
          info="Count of lead_cal bookings in the most recent calendar month present in the cohort. Slides with the cohort filter."
        />
        <KpiCard
          label="Demos linked to a booking"
          value={fmtPct(stats.demosWithMatch, stats.totalDemos)}
          sub={`${fmtN(stats.demosWithMatch)} / ${fmtN(stats.totalDemos)}`}
          tone="muted"
          info="Of demos in the dashboard's main cohort, share that have a matched lead_cal booking (lower-cased email ∈ attendee_emails, ±60 days)."
        />
        <KpiCard
          label="Bookings matched to a real demo"
          value={fmtPct(stats.matchedReal, stats.totalBookings)}
          sub={`${fmtN(stats.matchedReal)} / ${fmtN(stats.totalBookings)} bookings`}
          tone="muted"
          info="Of all lead_cal bookings, share that pair to a demo with is_demo=true. The remainder are either no-shows or unmatched (potentially a real demo we can't link)."
        />
        <KpiCard
          label="Bookings with UTM source"
          value={fmtPct(stats.withUtm, stats.totalBookings)}
          sub={`${fmtN(stats.withUtm)} / ${fmtN(stats.totalBookings)} bookings`}
          tone="muted"
          info="Data-quality KPI. The lower this is, the more channel attribution falls back to verbal mentions. Push above 80% via Shalin's UTM tagging plumbing."
        />
        <KpiCard
          label="No-show recovery candidates"
          value={fmtN(noShowRecovery)}
          sub="Booked, didn't sign up"
          tone="primary"
          info="Bookings where the demo was a no-show (or never paired to a demo) and the booker never signed up. Feed list to Loops / Slack outreach."
        />
      </div>

      <div className="chart-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, margin: "16px 16px 0" }}>
        <BookingsByChannelCard data={channelData} />
        <MatchedChannelFunnel rows={matchedChannelFunnel} />
      </div>

      <LeadCalDrilldown rows={leadCalRows} />
    </section>
  );
}

function BookingsByChannelCard({ data }: { data: { label: string; value: number }[] }) {
  return (
    <div style={{ position: "relative" }}>
      <HBarChart
        title="Bookings by channel"
        subtitle="Per reporting.lead_cal UTM source / campaign. Buckets scoped to channels lead_cal actually carries."
        data={data}
      />
      <div style={{ position: "absolute", top: 14, right: 14 }}>
        <InfoButton label="Booking attribution disclaimer" align="left">
          <p style={{ margin: 0 }}>
            Heavy disclaimer: demo-booking attribution is currently very incomplete. A large share of bookings carry no
            UTM source / campaign at all (see the "Bookings with UTM source" KPI), so this chart materially under-counts
            paid-channel and email contributions. Treat the absolute numbers as a floor, not the truth.
          </p>
        </InfoButton>
      </div>
    </div>
  );
}

function MatchedChannelFunnel({
  rows,
}: {
  rows: { label: string; bookings: number; signedUp: number; paid: number }[];
}) {
  if (rows.length === 0) {
    return (
      <div className="chart-tile">
        <h3>Booking → demo conversion (matched subset)</h3>
        <div className="empty" style={{ padding: 24 }}>No matched bookings.</div>
      </div>
    );
  }
  return (
    <div className="chart-tile">
      <h3 style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        Booking → demo conversion (matched subset)
        <InfoButton label="Matched subset disclaimer">
          <p style={{ margin: 0 }}>
            Only the ~44% of lead_cal bookings that pair to a real demo. The unmatched 56% may behave differently;
            don't use this view to size source ROI in isolation.
          </p>
        </InfoButton>
      </h3>
      <div className="sub">Per matched-channel funnel from booking → signed up → paid.</div>
      <div className="table-wrap" style={{ marginTop: 8 }}>
        <table className="t" style={{ width: "100%", fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Channel</th>
              <th style={{ textAlign: "right" }}>Bookings</th>
              <th style={{ textAlign: "right" }}>Signed up</th>
              <th style={{ textAlign: "right" }}>Paid</th>
              <th style={{ textAlign: "right" }}>Sign-up %</th>
              <th style={{ textAlign: "right" }}>Paid %</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td>{r.label}</td>
                <td style={{ textAlign: "right" }}>{fmtN(r.bookings)}</td>
                <td style={{ textAlign: "right" }}>{fmtN(r.signedUp)}</td>
                <td style={{ textAlign: "right" }}>{fmtN(r.paid)}</td>
                <td style={{ textAlign: "right" }}>{fmtPct(r.signedUp, r.bookings)}</td>
                <td style={{ textAlign: "right" }}>{fmtPct(r.paid, r.bookings)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const LEAD_CAL_COLUMNS: ColumnSpec<LeadCalRow>[] = [
  { key: "booked_at", label: "Booked", accessor: (r) => r.booked_at,
    render: (_r, v) => fmtDateShort(v == null ? null : String(v)) },
  { key: "client_email", label: "Client email", filterable: false, accessor: (r) => r.client_email },
  { key: "channel_bucket", label: "Channel", filterable: true, accessor: (r) => r.channel_bucket },
  { key: "utm_source", label: "UTM source", filterable: true, accessor: (r) => r.utm_source },
  { key: "utm_campaign", label: "UTM campaign", filterable: true, accessor: (r) => r.utm_campaign },
  { key: "meeting_type", label: "Meeting type", filterable: true, accessor: (r) => r.meeting_type },
  { key: "booking_country_code", label: "Country", filterable: true, accessor: (r) => r.booking_country_code },
  { key: "match_status", label: "Match",
    filterable: true,
    accessor: (r) => r.matched_real_demo ? "real_demo" : r.matched_no_show_only ? "no_show" : "unmatched",
    render: (r) => {
      if (r.matched_real_demo) return <span className="badge b-blue">Real demo</span>;
      if (r.matched_no_show_only) return <span className="badge b-gray">No-show</span>;
      return <span className="badge b-gray">Unmatched</span>;
    },
  },
  { key: "booker_signed_up", label: "Booker signed up",
    filterable: true,
    accessor: (r) => r.booker_signed_up ? "yes" : "no",
    render: (r) => r.booker_signed_up
      ? <i className="ti ti-check" style={{ color: "var(--color-teal)" }} />
      : <span className="muted">—</span> },
  { key: "booker_billing_plan", label: "Plan",
    filterable: true,
    accessor: (r) => r.booker_billing_plan,
    render: (r) => r.booker_billing_plan ?? <span className="muted">—</span> },
];

function LeadCalDrilldown({ rows }: { rows: LeadCalRow[] }) {
  return (
    <div style={{ marginTop: 16, padding: "0 16px 16px" }}>
      <h3 style={{ fontSize: 14, margin: "0 0 8px" }}>Lead_cal bookings drill-down</h3>
      <p className="sub" style={{ marginBottom: 8 }}>
        Every Cal.com booking in cohort, with match status and booker outcome. Use the column filters to scope to a
        channel or unmatched subset.
      </p>
      <FilterableTable
        rows={rows}
        columns={LEAD_CAL_COLUMNS}
        rowKey={(r) => r.lead_cal_external_id}
        searchAccessor={(r) => [
          r.client_email ?? "",
          r.utm_source ?? "",
          r.utm_campaign ?? "",
          r.channel_bucket,
        ].join(" ")}
        searchPlaceholder="Search email, UTM, channel"
        emptyMessage="No bookings match these filters."
      />
    </div>
  );
}
